import React from 'react';
import type { MemoizedFn } from 'memoize-one';
import memoize from 'memoize-one';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import type { AISnippetRendererProps, SnippetRendererProps } from '@atlaskit/link-provider/types';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { AISnippetWithRelayEnvironment } from '@atlassian/ai-snippet/ai-snippet';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { fg } from '@confluence/feature-gating';
import { SessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { SessionDataContext } from '@confluence/session-data/entry-points/SessionDataContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expVal } from '@confluence/feature-experiments';

const emojiWrapperStyles = css({
	display: 'inline-block',
	font: token('font.body.small'),
	justifyContent: 'center',
	overflow: 'hidden',
	margin: 'auto',
	minWidth: '16px',
});

const SmartCardEmojiWrapper: React.ComponentType<React.HTMLAttributes<HTMLSpanElement>> = ({
	children,
}) => <span css={emojiWrapperStyles}>{children}</span>;

// Custom render wrapper for AISnippet providing session data
const AISnippetRenderer = (props: SnippetRendererProps) => {
	const { displayName, userId, isAdminHubAIEnabled } = useSessionData();

	const isAIFeatureEnabled = isFeatureEnabled('confluence', 'confluence-ai');

	if (
		!isAdminHubAIEnabled ||
		!isAIFeatureEnabled ||
		!expVal('confluence-ai-linking-ai-snippets', 'should_render_ai_snippet', false)
	) {
		return props.fallbackComponent;
	}

	return (
		<AISnippetWithRelayEnvironment
			{...(props as AISnippetRendererProps)}
			userId={userId ?? undefined}
			displayName={displayName ?? undefined}
		/>
	);
};

export const getSmartCardRenderers: MemoizedFn<
	(this: any) => {
		renderers: {
			emoji: (emojiId: any, _parentComponent?: string, contentType?: string) => React.JSX.Element;
			snippet:
				| ((
						props: SnippetRendererProps,
				  ) =>
						| string
						| number
						| boolean
						| React.JSX.Element
						| Iterable<React.ReactNode>
						| null
						| undefined)
				| undefined;
		};
	}
> = memoize(
	(): {
		renderers: {
			emoji: (emojiId: any, _parentComponent?: string, contentType?: string) => React.JSX.Element;
			snippet:
				| ((
						props: SnippetRendererProps,
				  ) =>
						| string
						| number
						| boolean
						| React.JSX.Element
						| Iterable<React.ReactNode>
						| null
						| undefined)
				| undefined;
		};
	} => ({
		renderers: {
			emoji: (emojiId, _parentComponent = 'SmartCard', contentType = 'page') => (
				<SessionData>
					{(sessionData) => (
						<SessionDataContext.Provider value={sessionData}>
							<LazyEmojiComponentLoader
								emoji={emojiId}
								height={16}
								context="smartCard"
								wrapper={SmartCardEmojiWrapper}
								contentType={contentType}
							/>
						</SessionDataContext.Provider>
					)}
				</SessionData>
			),
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			snippet: fg('cc-ai-snippet-renderer-gate') ? AISnippetRenderer : undefined,
		},
	}),
);
